import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { Star, Plus, Check } from 'lucide-react';
import Analytics from '../services/analytics';
const ProductCard = ({ product, badge, isNew, isExclusive }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [addingToCart, setAddingToCart] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const { addToWishlist, removeFromWishlist, isWishlisted } = useWishlist();
  const { addToCart, cartItems } = useCart();
  const productId = product.id;
  const wishlisted = isWishlisted(productId);
  const exclusiveFlag = isExclusive ?? product.is_exclusive;
  // Determine out-of-stock status
  const isOutOfStock = product.has_variants
    ? (product.total_stock || 0) <= 0
    : (product.stock_quantity || 0) <= 0;
  // Check if item already in cart (product_id match works because single-variant/simple only one possible)
  const isInCart = cartItems?.some(item => Number(item.product_id) === Number(product.id));
  // Determine which action button to show
  const showSelectOptions = product.has_variants && (product.total_variants || 0) > 1;
  const showAddToCartButton = !product.has_variants || (product.total_variants || 0) === 1;

  return (
    <article className="group bg-[#fafafa] rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl flex flex-col h-full" aria-label={`${product.name} by ${product.brand}`}>
      <div className="relative aspect-square overflow-hidden bg-gray-100 flex-shrink-0">
        <button
          onClick={() => navigate(`/product/${product.id}`, { state: { product } })}
          className="w-full h-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold focus:ring-inset"
          aria-label={`View ${product.name} details`}
        >
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            if (wishlisted) {
              removeFromWishlist(productId);
            } else {
              addToWishlist({ ...product, id: productId });
              Analytics.wishlistAdd(product);
            }
          }}
          aria-label={wishlisted ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`}
          aria-pressed={wishlisted}
          className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 z-30 focus:outline-none focus:ring-2 focus:ring-gold"
        >
          <svg className={`w-4 h-4 ${wishlisted ? 'text-gold fill-current' : 'text-gray-400'}`} fill={wishlisted ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </button>
        {/* Badges */}
        {(badge || !!isNew || !!exclusiveFlag) && (
          <div className="absolute top-3 left-3 flex gap-2">
            {!!isNew && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                NEW
              </span>
            )}
            {!!exclusiveFlag && (
              <span className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded">
                ONLINE EXCLUSIVE
              </span>
            )}
            {badge && (
              <span className="bg-gold text-white text-xs font-bold px-2 py-1 rounded">
                {badge}
              </span>
            )}
          </div>
        )}
        {/* Out of Stock overlay */}
        {isOutOfStock && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-10">
            <span className="bg-white text-gray-900 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
              Out of Stock
            </span>
          </div>
        )}

        {!isOutOfStock && (
          <>
            {showSelectOptions ? (
              <Link
                to={`/product/${product.id}`}
                onClick={e => e.stopPropagation()}
                className="absolute top-[72%] -translate-y-1/2 right-0 z-20 flex items-center gap-1 pl-3 pr-2.5 py-2 bg-gold rounded-l-full shadow-md text-white text-[11px] font-bold"
              >
                Select
                <Plus size={14} className="text-white" />
              </Link>
            ) : showAddToCartButton ? (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (addingToCart || isInCart || isOutOfStock) return;
                  setAddingToCart(true);
                  try {
                    if (product.has_variants && product.total_variants === 1) {
                      try {
                        const res = await fetch(
                          `${process.env.REACT_APP_API_URL || 'http://localhost:3002'}/api/products/${product.id}/variants`
                        );
                        const variants = await res.json();
                        if (variants.length > 0) {
                          const variant = variants[0];
                          if (Number(variant.stock_quantity) <= 0) {
                            toast.error('Sorry, this item is out of stock');
                            return;
                          }
                          await addToCart({
                            ...product,
                            quantity: 1,
                            variant_id: variant.id,
                            sku: variant.variant_sku,
                            color: variant.color,
                            size: variant.size,
                            finalPrice: Number(product.price || 0) + Number(variant.price_adjustment || 0),
                            base_price: Number(product.price || 0) + Number(variant.price_adjustment || 0),
                            variant_stock_quantity: variant.stock_quantity,
                          });
                        }
                      } catch (err) {
                        await addToCart({
                          ...product,
                          finalPrice: product.sale_price || product.price,
                          base_price: product.price,
                        });
                      }
                    } else {
                      if (Number(product.stock_quantity) <= 0) {
                        toast.error('Sorry, this item is out of stock');
                        return;
                      }
                      await addToCart({
                        ...product,
                        finalPrice: product.sale_price || product.price,
                        base_price: product.price,
                      });
                    }
                    Analytics.addToCart(product);
                    setJustAdded(true);
                    setTimeout(() => setJustAdded(false), 1000);
                  } catch (err) {
                    console.error('Add to cart failed:', err);
                  } finally {
                    setAddingToCart(false);
                  }
                }}
                aria-label={`Add ${product.name} to cart`}
                className={`absolute top-[72%] -translate-y-1/2 right-0 z-20 flex items-center pl-3 ${justAdded ? 'pr-3.5' : 'pr-2.5'} py-2 bg-gold rounded-l-full shadow-md text-white`}
              >
                {justAdded ? (
                  <Check size={14} className="text-white" />
                ) : (
                  <Plus size={14} className="text-white" />
                )}
              </button>
            ) : null}
          </>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <button
          onClick={() => navigate(`/product/${product.id}`, { state: { product } })}
          className="text-left focus:outline-none focus:underline"
          aria-label={`View ${product.name} details`}
        >
          <p className="text-[10px] uppercase tracking-widest text-[#999] mb-1">{product.brand}</p>
          <h3 className="text-base font-semibold text-[#1a1a1a] mb-1 line-clamp-2">
            {product.name}
          </h3>
          {(product.average_rating > 0) && (
            <div className="flex items-center gap-1 mt-1">
              <div className="flex items-center">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-3 h-3 ${
                      star <= Math.round(product.average_rating)
                        ? 'fill-gold text-gold'
                        : 'fill-gray-200 text-gray-200'
                    }`}
                  />
                ))}
              </div>
              <span className="text-xs text-gray-500">
                ({product.review_count})
              </span>
            </div>
          )}
          {/* Price display — handles simple, variant, and discounted products */}
          <div className="mb-3">
            {product.discount_label ? (
              // ON SALE — show strikethrough original + sale price + badge
              <>
                {/* Original price — strikethrough */}
                <p className="text-xs text-gray-400 line-through leading-tight">
                  {product.has_variants === 1 && product.min_price !== product.max_price
                    ? `KSh ${Number(product.min_price || 0).toLocaleString('en-US')} - KSh ${Number(product.max_price || 0).toLocaleString('en-US')}`
                    : product.has_variants === 1
                      ? `KSh ${Number(product.min_price || 0).toLocaleString('en-US')}`
                      : `KSh ${Number(product.price || 0).toLocaleString('en-US')}`
                  }
                </p>
                {/* Sale price + badge */}
                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                  <p className="text-sm font-semibold text-gold">
                    {product.has_variants === 1 && product.sale_min_price !== product.sale_max_price
                      ? `KSh ${Number(product.sale_min_price || 0).toLocaleString('en-US')} - KSh ${Number(product.sale_max_price || 0).toLocaleString('en-US')}`
                      : product.has_variants === 1
                        ? `KSh ${Number(product.sale_min_price || 0).toLocaleString('en-US')}`
                        : `KSh ${Number(product.sale_price || 0).toLocaleString('en-US')}`
                    }
                  </p>
                  <span className="bg-amber-500 text-white text-xs font-bold
                    px-2 py-0.5 rounded">
                    {product.discount_label}
                  </span>
                </div>
              </>
            ) : (
              // NO DISCOUNT — original price display unchanged
              <p className="text-sm font-semibold text-gold">
                {product.has_variants === 1 && product.min_price !== product.max_price
                  ? `KSh ${Number(product.min_price || 0).toLocaleString('en-US')} - KSh ${Number(product.max_price || 0).toLocaleString('en-US')}`
                  : product.has_variants === 1
                    ? `KSh ${Number(product.min_price || 0).toLocaleString('en-US')}`
                    : `KSh ${Number(product.price || 0).toLocaleString('en-US')}`
                }
              </p>
            )}
          </div>
        </button>

      </div>
    </article>
  );
};
export default ProductCard;